import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { toast } from "sonner";
import { useLocation } from "wouter";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { trpc } from "@/lib/trpc";

type SiteRef = { id: number; name: string; siteCode: string };
type Site360Action = {
  id: number;
  actionType: string;
  description: string;
  status: string;
  priority: string;
  requestedDate: string;
  requesterName: string;
  responsibleUserId: number | null;
  responsibleName: string | null;
  installationId: number | null;
  installationCode: string | null;
  ticketReference?: string | null;
  installationName: string | null;
  contractId: number | null;
  contractCode: string | null;
  contractTitle: string | null;
};
type Site360Installation = {
  id: number;
  installationCode: string | null;
  ticketReference?: string | null;
  name: string | null;
  workOrderType: string;
  status: string;
  startDate: string | null;
  createdAt: string;
  contractId: number | null;
  contractCode: string | null;
  contractTitle: string | null;
  contractStatus: string | null;
};

const STATUS_OPTIONS = [
  ["open", "Abierta"],
  ["in_progress", "En curso"],
  ["completed", "Completada"],
  ["cancelled", "Cancelada"],
] as const;
const PRIORITY_OPTIONS = [
  ["low", "Baja"],
  ["normal", "Normal"],
  ["high", "Alta"],
  ["urgent", "Urgente"],
] as const;
const STATUS_LABELS: Record<string, string> = {
  open: "Abierta",
  in_progress: "En curso",
  completed: "Completada",
  cancelled: "Cancelada",
  pending: "Pendiente",
  active: "Activa",
};
const ROLE_LABELS: Record<string, string> = {
  owner: "Propietario",
  operator: "Operador",
  occupier: "Ocupante",
  manager: "Gestor",
  maintainer: "Mantenedor",
  customer: "Cliente",
  supplier_maintainer: "Mantenedor proveedor",
  primary: "Principal",
  billing: "Facturación",
  technical: "Técnico",
  sales: "Comercial",
  legal: "Legal",
  other: "Otro",
};
const DYNAMIC_TEXT_CLASS_NAME = "min-w-0 wrap-anywhere";
const DYNAMIC_BADGE_CLASS_NAME = "max-w-full whitespace-normal wrap-anywhere";
function localToday(): string {
  const date = new Date();
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 10);
}

function createEmptyActionForm() {
  return {
    description: "",
    priority: "normal",
    requestedDate: localToday(),
    requesterName: "",
    responsibleUserId: "",
    installationId: "",
    contractId: "",
  };
}

function addressLabel(site: any): string {
  return (
    [
      site.addressLine1,
      site.addressLine2,
      [site.postalCode, site.city].filter(Boolean).join(" "),
      site.countryCode,
    ]
      .filter(Boolean)
      .join(", ") || "Dirección no informada"
  );
}

function formatDate(value?: string | null): string {
  if (!value) return "—";
  const normalized = value.length === 10 ? `${value}T00:00:00` : value;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString("es-ES");
}

function fieldClassName(): string {
  return "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm";
}

function SiteActionFollowUp({
  action,
  responsibleUsers,
  canManage,
  onSave,
}: {
  action: Site360Action;
  responsibleUsers: Array<{ id: number; name: string }>;
  canManage: boolean;
  onSave: (
    actionId: number,
    values: {
      status: string;
      priority: string;
      responsibleUserId: number | null;
    }
  ) => Promise<void>;
}) {
  const [status, setStatus] = useState(action.status);
  const [priority, setPriority] = useState(action.priority);
  const [responsibleUserId, setResponsibleUserId] = useState(
    action.responsibleUserId == null ? "" : String(action.responsibleUserId)
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setStatus(action.status);
    setPriority(action.priority);
    setResponsibleUserId(
      action.responsibleUserId == null ? "" : String(action.responsibleUserId)
    );
  }, [action.status, action.priority, action.responsibleUserId]);

  async function save(): Promise<void> {
    setSaving(true);
    try {
      await onSave(action.id, {
        status,
        priority,
        responsibleUserId: responsibleUserId ? Number(responsibleUserId) : null,
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card className="border-amber-200">
      <CardContent className="@container/site-action-follow-up pt-4 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="destructive">Avería</Badge>
            <Badge variant="outline" className={DYNAMIC_BADGE_CLASS_NAME}>
              {STATUS_LABELS[action.status] ?? action.status}
            </Badge>
            <Badge variant="secondary">
              {formatDate(action.requestedDate)}
            </Badge>
          </div>
          <span
            className={`${DYNAMIC_TEXT_CLASS_NAME} text-xs text-muted-foreground`}
          >
            Solicitante: {action.requesterName}
          </span>
        </div>
        <p className={`${DYNAMIC_TEXT_CLASS_NAME} whitespace-pre-wrap text-sm`}>
          {action.description}
        </p>
        <div className="grid min-w-0 gap-2 text-xs text-muted-foreground sm:grid-cols-2">
          {action.ticketReference && (
            <span className={DYNAMIC_TEXT_CLASS_NAME}>Ticket: {action.ticketReference}</span>
          )}
          {action.installationId != null && (
            <span className={DYNAMIC_TEXT_CLASS_NAME}>
              Instalación relacionada:{" "}
              {action.installationCode ||
                action.installationName ||
                `#${action.installationId}`}
            </span>
          )}
          {action.contractId != null && (
            <span className={DYNAMIC_TEXT_CLASS_NAME}>
              Contrato relacionado:{" "}
              {action.contractCode ||
                action.contractTitle ||
                `#${action.contractId}`}
            </span>
          )}
          <span className={DYNAMIC_TEXT_CLASS_NAME}>
            Responsable: {action.responsibleName || "Sin asignar"}
          </span>
        </div>
        {canManage && (
          <div className="grid min-w-0 gap-2 @2xl/site-action-follow-up:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.4fr)_auto] @2xl/site-action-follow-up:items-end">
            <div className="min-w-0 space-y-1">
              <Label htmlFor={`site-action-status-${action.id}`}>Estado</Label>
              <select
                id={`site-action-status-${action.id}`}
                className={fieldClassName()}
                value={status}
                onChange={event => setStatus(event.target.value)}
              >
                {STATUS_OPTIONS.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <div className="min-w-0 space-y-1">
              <Label htmlFor={`site-action-priority-${action.id}`}>
                Prioridad
              </Label>
              <select
                id={`site-action-priority-${action.id}`}
                className={fieldClassName()}
                value={priority}
                onChange={event => setPriority(event.target.value)}
              >
                {PRIORITY_OPTIONS.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <div className="min-w-0 space-y-1">
              <Label htmlFor={`site-action-responsible-${action.id}`}>
                Responsable
              </Label>
              <select
                id={`site-action-responsible-${action.id}`}
                className={fieldClassName()}
                value={responsibleUserId}
                onChange={event => setResponsibleUserId(event.target.value)}
              >
                <option value="">Sin asignar</option>
                {responsibleUsers.map(user => (
                  <option key={user.id} value={String(user.id)}>
                    {user.name}
                  </option>
                ))}
              </select>
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="w-full @2xl/site-action-follow-up:w-auto"
              disabled={saving}
              onClick={() => void save()}
            >
              {saving ? "Guardando…" : "Guardar seguimiento"}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function Site360Dialog({
  partnerId,
  site,
  open,
  onOpenChange,
  canManage,
}: {
  partnerId: number;
  site: SiteRef | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  canManage: boolean;
}) {
  const [, setLocation] = useLocation();
  const utils = trpc.useUtils();
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(createEmptyActionForm);
  const siteId = site?.id ?? 0;
  const previousContext = useRef({ partnerId, siteId });

  function closeCreateForm(): void {
    setCreateOpen(false);
    setForm(createEmptyActionForm());
  }

  function handleCreateOpenChange(nextOpen: boolean): void {
    setCreateOpen(nextOpen);
    if (!nextOpen) setForm(createEmptyActionForm());
  }

  useEffect(() => {
    if (
      previousContext.current.partnerId === partnerId &&
      previousContext.current.siteId === siteId
    ) {
      return;
    }
    previousContext.current = { partnerId, siteId };
    setCreateOpen(false);
    setForm(createEmptyActionForm());
  }, [partnerId, siteId]);

  useEffect(() => {
    if (!open) {
      setCreateOpen(false);
      setForm(createEmptyActionForm());
    }
  }, [open]);

  const detailQuery = trpc.customers.getSite360.useQuery(
    { partnerId, siteId },
    { enabled: open && siteId > 0, retry: false }
  );
  const responsibleUsersQuery =
    trpc.customers.getSiteActionResponsibleUsers.useQuery(undefined, {
      enabled: open && canManage,
      retry: false,
    });
  const createAction = trpc.customers.createSiteAction.useMutation();
  const updateAction = trpc.customers.updateSiteAction.useMutation();

  const detail = detailQuery.data as any;
  const address = useMemo(
    () => (detail?.site ? addressLabel(detail.site) : ""),
    [detail?.site]
  );
  const history = useMemo(() => {
    const installations: Site360Installation[] = detail?.installations ?? [];
    const actions: Site360Action[] = detail?.actions ?? [];
    return [
      ...installations.map(item => ({
        kind: "installation" as const,
        date: item.startDate ?? item.createdAt,
        item,
      })),
      ...actions.map(item => ({
        kind: "action" as const,
        date: item.requestedDate,
        item,
      })),
    ].sort(
      (left, right) =>
        new Date(right.date).getTime() - new Date(left.date).getTime()
    );
  }, [detail?.installations, detail?.actions]);

  async function invalidateSite(): Promise<void> {
    await utils.customers.getSite360.invalidate({ partnerId, siteId });
  }

  async function handleCreateAction(
    event: FormEvent<HTMLFormElement>
  ): Promise<void> {
    event.preventDefault();
    if (!site) return;
    try {
      await createAction.mutateAsync({
        partnerId,
        siteId,
        description: form.description,
        priority: form.priority as "low" | "normal" | "high" | "urgent",
        requestedDate: form.requestedDate,
        requesterName: form.requesterName,
        responsibleUserId: form.responsibleUserId
          ? Number(form.responsibleUserId)
          : null,
        installationId: form.installationId
          ? Number(form.installationId)
          : null,
        contractId: form.contractId ? Number(form.contractId) : null,
      });
      await invalidateSite();
      closeCreateForm();
      toast.success("Avería registrada");
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "No se pudo registrar la avería"
      );
    }
  }

  async function handleUpdateAction(
    actionId: number,
    values: {
      status: string;
      priority: string;
      responsibleUserId: number | null;
    }
  ): Promise<void> {
    try {
      await updateAction.mutateAsync({
        partnerId,
        siteId,
        actionId,
        status: values.status as
          | "open"
          | "in_progress"
          | "completed"
          | "cancelled",
        priority: values.priority as "low" | "normal" | "high" | "urgent",
        responsibleUserId: values.responsibleUserId,
      });
      await invalidateSite();
      toast.success("Seguimiento actualizado");
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "No se pudo actualizar el seguimiento"
      );
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="grid-cols-1 min-w-0 max-h-[92vh] max-w-[calc(100%-2rem)] overflow-y-auto sm:max-w-4xl">
          <DialogHeader className="min-w-0">
            <DialogTitle className={`${DYNAMIC_TEXT_CLASS_NAME} pr-12`}>
              Sede 360
              {detail?.site
                ? ` · ${detail.site.name}`
                : site
                  ? ` · ${site.name}`
                  : ""}
            </DialogTitle>
            <DialogDescription className="min-w-0 break-words">
              Identidad, relaciones y actuaciones registradas en esta sede.
            </DialogDescription>
          </DialogHeader>
          {detailQuery.isLoading ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              Cargando sede…
            </div>
          ) : detailQuery.error ? (
            <div
              role="alert"
              className="py-8 text-center text-sm text-destructive"
            >
              No se pudo cargar la ficha de sede.
            </div>
          ) : detail?.site ? (
            <div className="min-w-0 space-y-4">
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex min-w-0 flex-wrap items-center justify-between gap-2">
                    <CardTitle className={`${DYNAMIC_TEXT_CLASS_NAME} text-lg`}>
                      {detail.site.name}
                    </CardTitle>
                    <div className="flex min-w-0 max-w-full flex-wrap items-center gap-2">
                      <Badge
                        variant="outline"
                        className={DYNAMIC_BADGE_CLASS_NAME}
                      >
                        {detail.site.siteCode}
                      </Badge>
                      <Badge
                        variant={detail.site.isActive ? "default" : "secondary"}
                      >
                        {detail.site.isActive ? "Activa" : "Inactiva"}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className={`${DYNAMIC_TEXT_CLASS_NAME} text-sm`}>
                    {address}
                  </p>
                  {detail.site.description && (
                    <p
                      className={`${DYNAMIC_TEXT_CLASS_NAME} text-sm text-muted-foreground`}
                    >
                      {detail.site.description}
                    </p>
                  )}
                  <div className="flex min-w-0 flex-wrap items-center gap-2 text-sm">
                    <span className="font-medium">Cliente:</span>
                    <span className={DYNAMIC_TEXT_CLASS_NAME}>
                      {detail.site.customerName}
                    </span>
                    <Badge
                      variant="outline"
                      className={DYNAMIC_BADGE_CLASS_NAME}
                    >
                      {ROLE_LABELS[detail.site.customerRole] ??
                        detail.site.customerRole}
                    </Badge>
                    <span
                      className={`${DYNAMIC_TEXT_CLASS_NAME} font-mono text-xs text-muted-foreground`}
                    >
                      {detail.site.customerPartnerCode}
                    </span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">
                    Relaciones de la sede
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex min-w-0 flex-wrap gap-2">
                    {(detail.roles ?? []).map((role: any) => (
                      <Badge
                        key={role.id}
                        variant="secondary"
                        className={DYNAMIC_BADGE_CLASS_NAME}
                      >
                        {ROLE_LABELS[role.role] ?? role.role}:{" "}
                        {role.partnerName}
                      </Badge>
                    ))}
                    {!(detail.roles ?? []).length && (
                      <span className="text-sm text-muted-foreground">
                        Sin relaciones activas.
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {(detail.installations ?? []).length} expedientes del
                    cliente vinculados por siteId.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">
                    Contactos asignados a esta sede
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {(detail.contacts ?? []).length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      No hay contactos asignados directamente a esta sede.
                    </p>
                  ) : (
                    (detail.contacts ?? []).map((contact: any) => (
                      <div
                        key={contact.id}
                        className="flex min-w-0 flex-wrap items-center justify-between gap-2 rounded-md border p-3"
                      >
                        <div className="min-w-0 max-w-full">
                          <div className="flex min-w-0 flex-wrap items-center gap-2">
                            <span
                              className={`${DYNAMIC_TEXT_CLASS_NAME} font-medium`}
                            >
                              {contact.displayName}
                            </span>
                            <Badge
                              variant="outline"
                              className={DYNAMIC_BADGE_CLASS_NAME}
                            >
                              {contact.role}
                            </Badge>
                            {contact.isPrimary && (
                              <Badge variant="secondary">Principal</Badge>
                            )}
                          </div>
                          {contact.jobTitle && (
                            <p
                              className={`${DYNAMIC_TEXT_CLASS_NAME} text-xs text-muted-foreground`}
                            >
                              {contact.jobTitle}
                            </p>
                          )}
                        </div>
                        <div
                          className={`${DYNAMIC_TEXT_CLASS_NAME} text-sm text-muted-foreground`}
                        >
                          {contact.primaryEmail && (
                            <div>{contact.primaryEmail}</div>
                          )}
                          {contact.primaryPhone && (
                            <div>{contact.primaryPhone}</div>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between gap-2">
                  <CardTitle className="text-base">
                    Averías e historial
                  </CardTitle>
                  {canManage && detail.site.isActive && (
                    <Button size="sm" onClick={() => setCreateOpen(true)}>
                      Nueva avería
                    </Button>
                  )}
                </CardHeader>
                <CardContent className="space-y-3">
                  {history.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      No hay expedientes ni actuaciones registrados en esta
                      sede.
                    </p>
                  ) : (
                    history.map(entry =>
                      entry.kind === "installation" ? (
                        <Card
                          key={`installation-${entry.item.id}`}
                          className="bg-muted/30 @container/site-history-item"
                        >
                          <CardContent className="flex min-w-0 flex-col items-stretch gap-3 py-3 @lg/site-history-item:flex-row @lg/site-history-item:items-center @lg/site-history-item:justify-between">
                            <div className="min-w-0 @lg/site-history-item:flex-1">
                              <div className="flex min-w-0 flex-wrap items-center gap-2">
                                <Badge variant="outline">Expediente</Badge>
                                <span
                                  className={`${DYNAMIC_TEXT_CLASS_NAME} font-medium`}
                                >
                                  {entry.item.ticketReference || entry.item.installationCode ||
                                    `#${entry.item.id}`}{" "}
                                  · {entry.item.name || "Instalación"}
                                </span>
                                <Badge
                                  variant="secondary"
                                  className={DYNAMIC_BADGE_CLASS_NAME}
                                >
                                  {STATUS_LABELS[entry.item.status] ??
                                    entry.item.status}
                                </Badge>
                              </div>
                              <p
                                className={`${DYNAMIC_TEXT_CLASS_NAME} mt-1 text-xs text-muted-foreground`}
                              >
                                {formatDate(entry.date)} ·{" "}
                                {entry.item.contractCode
                                  ? `Contrato ${entry.item.contractCode}`
                                  : "Sin contrato vinculado"}
                              </p>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              className="w-full @lg/site-history-item:w-auto"
                              onClick={() =>
                                setLocation(`/installations/${entry.item.id}`)
                              }
                            >
                              Abrir expediente
                            </Button>
                          </CardContent>
                        </Card>
                      ) : (
                        <SiteActionFollowUp
                          key={`action-${entry.item.id}`}
                          action={entry.item}
                          responsibleUsers={responsibleUsersQuery.data ?? []}
                          canManage={canManage}
                          onSave={handleUpdateAction}
                        />
                      )
                    )
                  )}
                  <p className="text-xs text-muted-foreground">
                    Los partes, materiales y documentos siguen en el expediente
                    original; esta vista no duplica sus registros ni archivos.
                  </p>
                </CardContent>
              </Card>

              {!!detail.contracts?.length && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">
                      Contratos del cliente disponibles para relacionar
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="flex flex-wrap gap-2">
                    {detail.contracts.map((contract: any) => (
                      <Button
                        key={contract.id}
                        variant="outline"
                        size="sm"
                        className="max-w-full whitespace-normal"
                        onClick={() =>
                          setLocation(
                            `/customers/${partnerId}/contracts/${contract.id}`
                          )
                        }
                      >
                        <span className="wrap-anywhere">
                          {contract.contractCode} · {contract.title}
                        </span>
                      </Button>
                    ))}
                  </CardContent>
                </Card>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={createOpen} onOpenChange={handleCreateOpenChange}>
        <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nueva avería</DialogTitle>
            <DialogDescription>
              Registra la solicitud en la sede. No se crea una instalación ni un
              contrato.
            </DialogDescription>
          </DialogHeader>
          <form
            className="space-y-4"
            onSubmit={event => void handleCreateAction(event)}
          >
            <div className="space-y-1.5">
              <Label htmlFor="site-action-description">Descripción</Label>
              <Textarea
                id="site-action-description"
                required
                maxLength={4000}
                value={form.description}
                onChange={event =>
                  setForm({ ...form, description: event.target.value })
                }
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="site-action-date">Fecha de solicitud</Label>
                <Input
                  id="site-action-date"
                  type="date"
                  required
                  value={form.requestedDate}
                  onChange={event =>
                    setForm({ ...form, requestedDate: event.target.value })
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="site-action-requester">Solicitante</Label>
                <Input
                  id="site-action-requester"
                  required
                  maxLength={255}
                  value={form.requesterName}
                  onChange={event =>
                    setForm({ ...form, requesterName: event.target.value })
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="site-action-create-priority">Prioridad</Label>
                <select
                  id="site-action-create-priority"
                  className={fieldClassName()}
                  value={form.priority}
                  onChange={event =>
                    setForm({ ...form, priority: event.target.value })
                  }
                >
                  {PRIORITY_OPTIONS.map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="site-action-create-responsible">
                  Responsable (opcional)
                </Label>
                <select
                  id="site-action-create-responsible"
                  className={fieldClassName()}
                  value={form.responsibleUserId}
                  onChange={event =>
                    setForm({ ...form, responsibleUserId: event.target.value })
                  }
                >
                  <option value="">Sin asignar</option>
                  {(responsibleUsersQuery.data ?? []).map((user: any) => (
                    <option key={user.id} value={String(user.id)}>
                      {user.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="site-action-create-installation">
                  Instalación FV existente (opcional)
                </Label>
                <select
                  id="site-action-create-installation"
                  className={fieldClassName()}
                  value={form.installationId}
                  onChange={event =>
                    setForm({ ...form, installationId: event.target.value })
                  }
                >
                  <option value="">Sin instalación vinculada</option>
                  {(detail?.installations ?? [])
                    .filter(
                      (item: Site360Installation) =>
                        item.workOrderType === "installation"
                    )
                    .map((item: Site360Installation) => (
                      <option key={item.id} value={String(item.id)}>
                        {item.ticketReference || item.installationCode || `#${item.id}`} ·{" "}
                        {item.name || "Instalación FV"}
                      </option>
                    ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="site-action-create-contract">
                  Contrato existente (opcional)
                </Label>
                <select
                  id="site-action-create-contract"
                  className={fieldClassName()}
                  value={form.contractId}
                  onChange={event =>
                    setForm({ ...form, contractId: event.target.value })
                  }
                >
                  <option value="">Sin contrato vinculado</option>
                  {(detail?.contracts ?? []).map((contract: any) => (
                    <option key={contract.id} value={String(contract.id)}>
                      {contract.contractCode} · {contract.title}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeCreateForm}>
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={createAction.isPending || !detail?.site?.isActive}
              >
                {createAction.isPending ? "Registrando…" : "Registrar avería"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
